import { Pencil } from 'lucide-react';
import { Link } from 'react-router';
import type { PaginatedResponse, Proposal } from '@/api/commercial/proposals';
import { ProposalStatus } from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatLgpdCnpj, formatLgpdCpf } from '@/lib/format-lgpd';

interface ProposalTableProps {
  data: PaginatedResponse<Proposal>;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const STATUS_LABEL: Record<ProposalStatus, { label: string; color: string }> = {
  [ProposalStatus.OPEN]: {
    label: 'Aberta',
    color: 'bg-yellow-100 text-yellow-800',
  },
  [ProposalStatus.PENDING_ACCEPT]: {
    label: 'Aguardando aceite',
    color: 'bg-blue-100 text-blue-800',
  },
  [ProposalStatus.PENDING_CREDIT]: {
    label: 'Aguardando Análise de Crédito',
    color: 'bg-blue-100 text-blue-800',
  },
  [ProposalStatus.FINISHED]: {
    label: 'Finalizada',
    color: 'bg-green-100 text-green-800',
  },
  [ProposalStatus.CANCELED]: {
    label: 'Cancelada',
    color: 'bg-red-100 text-red-800',
  },
};

export function ProposalTable({ data, currentPage, onPageChange }: ProposalTableProps) {
  const permissions = { fullLgpdAccess: true };

  const formatDocument = (cpfCnpj: string): string => {
    if (!cpfCnpj) return '-';
    const value = cpfCnpj.replace(/\D/g, '');
    const isCpf = value.length === 11;

    if (isCpf) {
      return formatLgpdCpf(value, permissions);
    } else {
      return formatLgpdCnpj(value, permissions);
    }
  };

  const formatDateTime = (value?: string | Date): string => {
    if (!value) return '-';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const resolveStatus = (status?: ProposalStatus | string) => {
    if (!status) {
      return { label: '-', color: 'bg-gray-100 text-gray-800' };
    }

    if (typeof status === 'string' && status in STATUS_LABEL) {
      return STATUS_LABEL[status as ProposalStatus];
    }

    return (
      STATUS_LABEL[status as ProposalStatus] ?? {
        label: status,
        color: 'bg-gray-100 text-gray-800',
      }
    );
  };

  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PROPOSTA</TableHead>
              <TableHead>CLIENTE</TableHead>
              <TableHead>CPF/CNPJ</TableHead>
              <TableHead>DATA DE ABERTURA</TableHead>
              <TableHead>VENDEDOR</TableHead>
              <TableHead>STATUS</TableHead>
              <TableHead className="text-right">AÇÃO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Nenhuma proposta encontrada
                </TableCell>
              </TableRow>
            ) : (
              docs.map((proposal) => {
                const status = resolveStatus(proposal.status);
                return (
                  <TableRow key={proposal.id}>
                    <TableCell>{proposal.seq}</TableCell>
                    <TableCell>
                      <span className="font-medium">{proposal.clientName || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDocument(proposal.clientCpfCnpj)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDateTime(proposal.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{proposal.userName || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/commercial/proposal/form/update/${proposal.id}`}>
                        <Button
                          title="Editar proposta"
                          variant="ghost"
                          className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.pages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />
    </div>
  );
}
